/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Unified Player Adapter
 *
 * Provides a consistent interface for all video players (Vimeo, YouTube, etc.).
 * Uses provider-specific player adapters (VimeoPlayer, YoutubePlayer) instead of switch statements.
 *
 * @typedef {import('@amblique/base/app/plugins/video/models/vimeo-player').VimeoPlayer} VimeoPlayer
 * @typedef {import('@amblique/base/app/plugins/video/models/youtube-player').YoutubePlayer} YoutubePlayer
 */
export class Player {
  /**
   * @param {VimeoPlayer | YoutubePlayer} adapter - Provider-specific player adapter
   */
  constructor(adapter) {
    this.adapter = adapter;
  }

  /**
   * Add an event listener for the specified event
   *
   * @param {string} event - The event name
   * @param {Function} listener - The event listener
   * @returns {void}
   */
  on(event, listener) {
    return this.adapter.on(event, listener);
  }

  /**
   * Remove an event listener for the specified event.
   * Will remove all listeners for that event if a `listener` isn't passed
   *
   * @param {string} event - The event name
   * @param {Function} [listener] - The event listener
   * @returns {void}
   */
  off(event, listener) {
    return this.adapter.off(event, listener);
  }

  /**
   * Load new video by ID
   *
   * @param {string|number} id - The video ID to load
   * @returns {Promise<void>}
   */
  async loadVideo(id) {
    return this.adapter.loadVideo(id);
  }

  /**
   * Gets the playing state of the current player
   *
   * @returns {Promise<boolean>} `true` if playback is playing
   */
  async isPlaying() {
    return this.adapter.isPlaying();
  }

  /**
   * Gets the paused state of the current player
   *
   * @returns {Promise<boolean>} `true` if playback is paused
   */
  async isPaused() {
    return this.adapter.isPaused();
  }

  /**
   * Gets the ended state of the current player
   *
   * @returns {Promise<boolean>} `true` if playback is ended
   */
  async isEnded() {
    return this.adapter.isEnded();
  }

  /**
   * Enable or disable the `autopause` behavior of this player
   *
   * @param {boolean} autopause - Enable autopause
   * @returns {Promise<void>}
   */
  async setAutopause(autopause) {
    return this.adapter.setAutopause(autopause);
  }

  /**
   * Set the color of this player to a hex or rgb string.
   * Setting the color may fail if the owner of the video has set their embed preferences to force a specific color.
   *
   * @param {string} color - Hex or rgb color string
   * @returns {Promise<void>}
   */
  async setColor(color) {
    return this.adapter.setColor(color);
  }

  /**
   * Set the loop state of the player.
   * When set to `true`, the player will start over immediately once playback ends.
   *
   * **Note:** when `loop` is turned on, the `ended` event will not fire for Vimeo player.
   *
   * @param {boolean} loop - Enable loop
   * @returns {Promise<void>}
   */
  async setLoop(loop) {
    return this.adapter.setLoop(loop);
  }

  /**
   * Gets the muted status of the current player
   *
   * @returns {Promise<boolean>}
   */
  async isMuted() {
    return this.adapter.isMuted();
  }

  /**
   * Set the muted state of the player.
   * When set to `true`, the player volume will be muted.
   *
   * @param {boolean} value - Mute or unmute
   * @returns {Promise<void>}
   */
  async setMuted(value) {
    return this.adapter.setMuted(value);
  }

  /**
   * Set the playback rate of the player
   *
   * @param {number} value - Playback rate
   * @returns {Promise<void>}
   */
  async setPlaybackRate(value) {
    return this.adapter.setPlaybackRate(value);
  }

  /**
   * Set the volume of the player
   *
   * @param {number} level - Volume level
   * @returns {Promise<void>}
   */
  async setVolume(level) {
    return this.adapter.setVolume(level);
  }

  /**
   * Unload the internal player (iframe) to its initial state
   *
   * @returns {Promise<void>}
   */
  async unload() {
    return this.adapter.unload();
  }

  /**
   * Cleanup the player and remove it from the DOM
   *
   * @returns {Promise<void>}
   */
  async destroy() {
    return this.adapter.destroy();
  }

  /**
   * Mute the video
   *
   * @returns {Promise<void>}
   */
  async mute() {
    return this.adapter.mute();
  }

  /**
   * Unmute the video
   *
   * @returns {Promise<void>}
   */
  async unMute() {
    return this.adapter.unMute();
  }

  /**
   * Plays the currently loaded video
   *
   * @returns {Promise<void>}
   */
  async play() {
    return this.adapter.play();
  }

  /**
   * Pause the video if it's playing
   *
   * @returns {Promise<void>}
   */
  async pause() {
    return this.adapter.pause();
  }

  /**
   * Stops and cancels loading of the current video
   *
   * @returns {Promise<void>}
   */
  async stop() {
    return this.adapter.stop();
  }

  /**
   * Call a method using directly inner player.
   * You should check the player type yourself before execute.
   *
   * @param {string} method - The method name
   * @param {...any} args - List of arguments
   * @returns {Promise<any>}
   */
  async call(method, ...args) {
    return this.adapter.call(method, ...args);
  }
}
